// API utility - Supports JWT authentication via HttpOnly cookies

const IS_DEV = process.env.NODE_ENV === 'development';

export const getCsrfToken = () => {
    const name = 'csrftoken';
    const cookieValue = document.cookie.split('; ').find(row => row.startsWith(name + '='));
    return cookieValue ? cookieValue.split('=')[1] : '';
};

export const getApiUrl = (path) => {
    return path.startsWith('/') ? path : `/${path}`;
};

const SUPPORTS_BINARY_GZIP = !IS_DEV && typeof DecompressionStream !== 'undefined';

export const apiFetch = async (url, options = {}) => {
    const defaultHeaders = {};

    if (!(options.body instanceof FormData)) {
        defaultHeaders['Content-Type'] = 'application/json';
    }

    if (options.method && options.method !== 'GET') {
        defaultHeaders['X-CSRFToken'] = getCsrfToken();
    }

    if (SUPPORTS_BINARY_GZIP) {
        defaultHeaders['X-Accept-Binary'] = 'true';
    }

    const config = {
        ...options,
        credentials: 'include',
        headers: {
            ...defaultHeaders,
            ...options.headers,
        },
    };

    try {
        let response = await fetch(url, config);

        if (SUPPORTS_BINARY_GZIP && response.headers.get('X-Compression') === 'gzip-binary') {
            const ds = new DecompressionStream('gzip');
            response = new Response(response.body.pipeThrough(ds), {
                status: response.status,
                statusText: response.statusText,
                headers: { 'Content-Type': 'application/json' },
            });
        }

        if (response.status === 401 || response.status === 403) {
            // Always clear any cached user
            localStorage.removeItem('user');

            const path = window.location.pathname || '';
            const isAuthPage = path.includes('login') || path.includes('signup');

            // On login/signup pages, do NOT redirect again to avoid flicker/loops.
            if (!isAuthPage) {
                if (response.status === 403) {
                    window.location.href = `/login`;
                } else {
                    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
                    window.location.href = `/login?returnUrl=${returnUrl}`;
                }
            }

            throw new Error('Your session is not valid. Please login again.');
        }

        if (!response.ok) {
            const status = response.status;
            const fallback =
                status >= 500
                    ? 'Something went wrong on the server. Please try again in a moment.'
                    : 'We could not complete this request. Please try again.';

            let message = fallback;
            try {
                const text = await response.text();
                const trimmed = (text || '').trim();
                if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                    const data = JSON.parse(trimmed);
                    if (typeof data.error === 'string' && data.error) {
                        message = data.error;
                    } else if (typeof data.message === 'string' && data.message) {
                        message = data.message;
                    } else if (typeof data.detail === 'string' && data.detail) {
                        message = data.detail;
                    }
                }
            } catch {
                /* HTML debug pages or non-JSON: keep fallback */
            }
            throw new Error(message);
        }

        return response;
    } catch (error) {
        // Re-throw the error so callers can handle it
        throw error;
    }
};

export const api = {
    get: (url, options = {}) => apiFetch(url, { ...options, method: 'GET' }),

    post: (url, data, options = {}) => {
        const isFormData = data instanceof FormData;
        return apiFetch(url, {
            ...options,
            method: 'POST',
            body: isFormData ? data : JSON.stringify(data),
            headers: {
                ...options.headers,
            }
        });
    },

    put: (url, data, options = {}) => {
        const isFormData = data instanceof FormData;
        return apiFetch(url, {
            ...options,
            method: 'PUT',
            body: isFormData ? data : JSON.stringify(data),
            headers: {
                ...options.headers,
            }
        });
    },

    patch: (url, data, options = {}) => {
        const isFormData = data instanceof FormData;
        return apiFetch(url, {
            ...options,
            method: 'PATCH',
            body: isFormData ? data : JSON.stringify(data),
            headers: {
                ...options.headers,
            }
        });
    },

    delete: (url, options = {}) => apiFetch(url, {
        ...options,
        method: 'DELETE',
    }),
};

export default api;
